import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, errorMessage } from "../api";
import { loadInstance, setMe } from "../store/app";
import { useApp } from "../store/hooks";

export function AuthPage({ mode }: { mode: "login" | "setup" | "register" }) {
  const instance = useApp((s) => s.instance);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isRegister = mode !== "login";
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const me = isRegister
        ? await api.register({
            email,
            password,
            display_name: displayName || email.split("@")[0],
            ...(mode === "setup" && orgName.trim() ? { org_name: orgName.trim() } : {}),
          })
        : await api.login({ email, password });
      setMe(me);
      if (mode === "setup") await loadInstance().catch(() => undefined);
      navigate(from === "/login" || from === "/setup" ? "/" : from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const title =
    mode === "setup" ? "Welcome to ToDoItAll" : mode === "register" ? "Create account" : "Sign in";

  return (
    <main className="auth-page">
      <form className="auth-card" onSubmit={submit} aria-labelledby="auth-title">
        <div className="auth-logo" aria-hidden="true">
          ✓
        </div>
        <h1 id="auth-title">{title}</h1>
        {mode === "setup" && (
          <p className="muted">
            First run: create the administrator account and your organization.
          </p>
        )}
        {instance?.name && mode === "login" && <p className="muted">{instance.name}</p>}

        {mode === "setup" && (
          <label className="field">
            <span>Organization name</span>
            <input
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Acme Inc. or your name"
              required
              autoComplete="organization"
            />
          </label>
        )}
        {isRegister && (
          <label className="field">
            <span>Your name</span>
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              autoComplete="name"
            />
          </label>
        )}
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            autoFocus={mode === "login"}
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={isRegister ? 8 : undefined}
            autoComplete={isRegister ? "new-password" : "current-password"}
          />
        </label>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn primary block" disabled={busy}>
          {busy ? "Please wait…" : mode === "setup" ? "Create organization" : isRegister ? "Create account" : "Sign in"}
        </button>
        {mode === "login" && (
          <p className="muted small center">
            No account?{" "}
            <button type="button" className="btn-link" onClick={() => navigate("/register")}>
              Register
            </button>
          </p>
        )}
        {mode === "register" && (
          <p className="muted small center">
            <button type="button" className="btn-link" onClick={() => navigate("/login")}>
              Back to sign in
            </button>
          </p>
        )}
      </form>
    </main>
  );
}
